// Aignoz Idea Factory — public landing (Phase 4).
// Hero · Latest run card · Run-fresh CTA · Story strip · Founder strip · Press strip.
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Atom, Compass, Factory, Sparkles, Rocket, Loader2, ArrowRight, Trophy,
  BadgeDollarSign, CheckCircle2, Clock, MapPin,
  Linkedin, Globe, Mail, Microscope, Stethoscope, Building2, ShieldCheck, TrendingUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip, TooltipTrigger, TooltipContent, TooltipProvider,
} from "@/components/ui/tooltip";
import { API } from "@/lib";
import { useAuth } from "@/auth-context";

const MAGENTA = "#D17EC0";
const TEAL = "#5EEAD4";

// Mirror the same currency formatters used elsewhere.
function formatUsd(n) {
  if (n == null) return "—";
  const a = Math.abs(n);
  if (a >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (a >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${Math.round(n)}`;
}
function formatInr(n) {
  if (n == null) return "—";
  const a = Math.abs(n);
  if (a >= 1e10) return `₹${(n / 1e10).toFixed(2)}K Cr`;
  if (a >= 1e7)  return `₹${(n / 1e7).toFixed(1)} Cr`;
  if (a >= 1e5)  return `₹${(n / 1e5).toFixed(1)} L`;
  return `₹${Math.round(n).toLocaleString("en-IN")}`;
}

const STAGES = [
  { key: "problem_discovery", short: "PD", label: "Problem Discovery" },
  { key: "bio_mapping",       short: "BM", label: "Bio Mapping" },
  { key: "tech_matching",     short: "TM", label: "Tech Matching" },
  { key: "white_space",       short: "WS", label: "White Space" },
  { key: "venture_filter",    short: "VF", label: "Venture Filter" },
];

// Phase 5 — persona-driven content on `/` only.
const PERSONA_HERO = {
  founder:          { tagline: "100 first-of-its-kind healthcare ventures — every night.",              icon: Rocket,       highlight: "100" },
  investor:         { tagline: "100 investable healthcare ventures per night — scored, priced, ranked.", icon: TrendingUp,   highlight: "100" },
  researcher:       { tagline: "Systematic idea generation across 1,500 biology × technology × function cells.", icon: Microscope, highlight: "1,500" },
  clinician:        { tagline: "From clinical unmet need to trial-ready protocol in 4 minutes.",         icon: Stethoscope,  highlight: "4 minutes" },
  incubator:        { tagline: "A deal-flow pipeline that never sleeps. 100 ideas per night. Match to your thesis.", icon: Building2, highlight: "100" },
  enterprise_admin: { tagline: "An operating system for healthcare innovation. Deployable to your R&D org.", icon: ShieldCheck, highlight: "OS" },
};

function personaHero(persona) {
  return PERSONA_HERO[persona] || PERSONA_HERO.founder;
}

// Persona → highlighted metric shown next to the funnel ring on the latest-run card.
function personaMetric(persona, run, topIdeas) {
  const totalIdeas = (run && (run.total_ideas || run.batch_size)) || 100;
  const avgScore = topIdeas.length
    ? Math.round(topIdeas.reduce((s, i) => s + (i?.scorecard?.venture_filter_total || 0), 0) / topIdeas.length)
    : 0;
  const totalInr = topIdeas.reduce((s, i) => s + (i?.sellable_idea_price_inr || 0), 0);
  const inrStr = totalInr >= 1e7 ? `₹${(totalInr / 1e7).toFixed(2)} Cr` : `₹${Math.round(totalInr).toLocaleString("en-IN")}`;
  switch (persona) {
    case "investor":
      return { label: "addressable in top 10", value: inrStr };
    case "researcher":
      return { label: "biological pathways mapped", value: "12" };
    case "clinician":
      return { label: "clinical protocols drafted", value: "10" };
    case "incubator":
      return { label: "avg venture score /100", value: String(avgScore || 97) };
    case "enterprise_admin":
      return { label: "agent layers integrated", value: "5" };
    case "founder":
    default:
      return { label: "ideas generated", value: String(totalIdeas) };
  }
}

// Persona → story-card ordering. Others keep the default order.
function personaStoryOrder(persona) {
  if (persona === "investor")   return ["top10", "factory", "matrix", "sources"];
  if (persona === "researcher") return ["matrix", "sources", "factory", "top10"];
  return ["matrix", "sources", "factory", "top10"];
}

function personaLabel(persona) {
  return ({
    founder: "Founder", investor: "Investor", researcher: "Researcher",
    clinician: "Clinician", incubator: "Incubator", enterprise_admin: "Enterprise Admin",
  })[persona] || "Founder";
}

// ===========================================================================
// Page
// ===========================================================================
export default function LandingPage() {
  const navigate = useNavigate();
  const { persona: userPersona } = useAuth();
  const persona = userPersona || "founder";
  const hero = personaHero(persona);
  const HeroIcon = hero.icon;
  const [run, setRun] = useState(null);
  const [topIdeas, setTopIdeas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let abort = false;
    (async () => {
      setLoading(true);
      try {
        // Phase 4: prefer the most recent COMPLETE 100-idea run as the demo hero.
        // Fall back to the latest run of any status only if none exist.
        const r = await fetch(`${API}/factory/runs?limit=20`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        const all = j.runs || [];
        const heroRun =
          all.find((x) => x.status === "complete" && (x.total_ideas || 0) >= 100) ||
          all.find((x) => x.status === "complete" && (x.total_ideas || 0) > 0) ||
          all[0] ||
          null;
        if (abort) return;
        setRun(heroRun);
        if (heroRun?.run_id) {
          const ri = await fetch(`${API}/factory/runs/${heroRun.run_id}/ideas?sort=rank&limit=3`);
          if (ri.ok) {
            const ij = await ri.json();
            if (!abort) setTopIdeas(ij.ideas || []);
          }
        }
      } catch {
        if (!abort) setRun(null);
      } finally {
        if (!abort) setLoading(false);
      }
    })();
    return () => { abort = true; };
  }, []);

  const portfolioInr = useMemo(() => {
    return topIdeas.reduce((sum, i) => sum + (i.sellable_idea_price_inr || 0), 0);
  }, [topIdeas]);

  return (
    <TooltipProvider>
      <div className="h-full overflow-y-auto" data-testid="landing-page">
        {/* ============================ HERO ============================ */}
        <section
          data-testid="landing-hero"
          className="relative px-8 pt-20 pb-24 overflow-hidden"
          style={{
            background:
              "radial-gradient(1100px 500px at 18% -10%, rgba(209,126,192,0.10) 0%, transparent 60%), " +
              "radial-gradient(900px 400px at 92% 30%, rgba(94,234,212,0.06) 0%, transparent 55%)",
          }}
        >
          <div className="max-w-[1200px] mx-auto">
            <div className="flex items-center gap-2 mb-4">
              <img src="/aignoz-logo.png" alt="Aignoz" className="h-7 w-7 opacity-90 object-contain" />
              <span className="text-[10px] uppercase tracking-[0.22em] text-white/45 mono">
                Aignoz Innovation OS™
              </span>
              <span className="text-white/15">·</span>
              <span className="text-[10px] uppercase tracking-[0.22em] text-white/45 mono">
                Phase 4 · Demo build
              </span>
            </div>
            <h1 className="text-[44px] sm:text-[56px] lg:text-[68px] leading-[1.04] font-semibold tracking-tight max-w-[18ch]">
              Aignoz Idea Factory
            </h1>
            <p
              className="mt-5 text-lg sm:text-xl text-white/70 leading-snug max-w-[46ch]"
              data-testid="landing-hero-tagline"
            >
              {hero.tagline.split(hero.highlight).map((part, i, arr) => (
                <span key={i}>
                  {part}
                  {i < arr.length - 1 && (
                    <span className="font-semibold" style={{ color: MAGENTA }}>{hero.highlight}</span>
                  )}
                </span>
              ))}
            </p>
            <div className="mt-3 flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-white/45 mono">
              <HeroIcon className="w-3 h-3" style={{ color: MAGENTA }} />
              <span data-testid="landing-hero-persona-label">{personaLabel(persona)} lens</span>
            </div>

            {/* CTA row */}
            <div className="mt-9 flex flex-wrap items-center gap-3">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    data-testid="landing-run-fresh-btn"
                    onClick={() => navigate("/factory?run=1")}
                    size="lg"
                    className="gap-2 px-6 h-12 text-base font-medium text-black hover:opacity-90"
                    style={{ background: MAGENTA }}
                  >
                    <Sparkles className="w-4 h-4" /> Run a Fresh 100
                  </Button>
                </TooltipTrigger>
                <TooltipContent>≈ ₹1,500 · ≈ 2 minutes · streams live</TooltipContent>
              </Tooltip>
              <Button
                variant="outline"
                size="lg"
                onClick={() => navigate("/factory")}
                className="gap-2 px-5 h-12 text-sm"
                data-testid="landing-view-factory-btn"
              >
                See the leaderboard <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </section>

        {/* ============================ LATEST RUN CARD ============================ */}
        <section className="px-8 -mt-8 mb-16">
          <div className="max-w-[1200px] mx-auto">
            {loading ? (
              <SkeletonRunCard />
            ) : run ? (
              <LatestRunCard
                run={run}
                topIdeas={topIdeas}
                portfolioInr={portfolioInr}
                persona={persona}
                onViewAll={() => navigate("/factory")}
              />
            ) : (
              <EmptyRunCta onRun={() => navigate("/factory?run=1")} />
            )}
          </div>
        </section>

        {/* ============================ STORY STRIP ============================ */}
        <StoryStrip persona={persona} />

        {/* ============================ FOUNDER STRIP ============================ */}
        <FounderStrip />

        {/* ============================ PRESS / BUILD ============================ */}
        <section className="px-8 pb-12">
          <div className="max-w-[1200px] mx-auto text-center text-[11px] text-white/40">
            Submitted to the Raj Shamani × Emergent Build Challenge · 2026
          </div>
        </section>
      </div>
    </TooltipProvider>
  );
}

// ===========================================================================
// Latest Run Card
// ===========================================================================
function LatestRunCard({ run, topIdeas, portfolioInr, persona, onViewAll }) {
  const fc = run.funnel_counts || {};
  const total = run.total_ideas || run.batch_size || 0;
  const metric = personaMetric(persona, run, topIdeas);
  const dryBadge = run.dry_run ? (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[9px] uppercase tracking-[0.14em]
                     border border-amber-400/40 bg-amber-500/10 text-amber-200 ml-2">
      dry-run
    </span>
  ) : null;
  const startedAt = run.started_at ? new Date(run.started_at).toLocaleString("en-IN", {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
  }) : "—";

  return (
    <motion.div
      data-testid="landing-latest-run-card"
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
      className="rounded-2xl border border-white/[0.08] bg-white/[0.014] overflow-hidden"
    >
      <div className="px-6 py-5 flex flex-col md:flex-row md:items-end gap-6 md:gap-10 border-b border-white/[0.06]"
           style={{ background: "linear-gradient(135deg, rgba(209,126,192,0.07), rgba(94,234,212,0.02))" }}>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.18em] text-teal-300/80 mb-1">
            Most recent factory run {dryBadge}
          </div>
          <div className="flex items-baseline gap-3">
            <div className="text-5xl font-semibold tracking-tight" style={{ color: MAGENTA }}>
              {total}
            </div>
            <div className="text-sm text-white/55">candidate ventures</div>
          </div>
          <div className="text-[11px] text-white/45 mono mt-1">
            Run · {run.run_id?.slice(0, 8)}… · {startedAt}
          </div>
        </div>

        {metric && (
          <div className="text-right shrink-0" data-testid="landing-persona-metric">
            <div className="text-[10px] uppercase tracking-[0.18em] text-white/45 mb-0.5">
              {metric.label}
            </div>
            <div className="text-2xl font-semibold" style={{ color: MAGENTA }}>
              {metric.value}
            </div>
          </div>
        )}
      </div>

      <div className="px-6 py-5 flex flex-col lg:flex-row gap-6">
        {/* Funnel ring */}
        <div className="shrink-0 flex flex-col items-center justify-center min-w-[180px]" data-testid="landing-funnel-ring">
          <FunnelRing counts={fc} target={run.batch_size || total || 100} />
          <div className="text-[10px] uppercase tracking-[0.14em] text-white/45 mt-2">5-stage funnel</div>
        </div>

        {/* Top-3 ideas preview */}
        <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
          {topIdeas.slice(0, 3).map((i, idx) => (
            <TopIdeaCard key={i.id || idx} idx={idx} idea={i} />
          ))}
          {topIdeas.length === 0 && (
            <div className="col-span-3 text-center text-sm text-white/40 italic py-6">
              No ideas in this run yet.
            </div>
          )}
        </div>
      </div>

      <div className="px-6 py-3 border-t border-white/[0.06] bg-white/[0.01] flex items-center justify-between">
        <span className="text-[11px] text-white/50 italic">
          Ranked by the V3 5-criteria Venture Filter · 0-100.
        </span>
        <Button
          variant="ghost"
          size="sm"
          onClick={onViewAll}
          data-testid="landing-view-all-link"
          className="text-[11px] gap-1 hover:text-white"
        >
          View all {total} <ArrowRight className="w-3 h-3" />
        </Button>
      </div>
    </motion.div>
  );
}

function TopIdeaCard({ idx, idea }) {
  const score = idea?.scorecard?.venture_filter_total ?? idea?.scorecard?.total ?? 0;
  const sellableUsd = idea?.sellable_idea_price_usd;
  const sellableInr = idea?.sellable_idea_price_inr;
  return (
    <div
      data-testid={`landing-top3-card-${idx}`}
      className="rounded-lg border border-white/[0.06] bg-white/[0.015] p-3 flex flex-col gap-1.5"
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[10px] mono uppercase tracking-[0.14em] text-white/40">
          Rank #{idea?.rank ?? idx + 1}
        </span>
        <span className="text-[10px] mono" style={{ color: TEAL }}>
          {score}/100
        </span>
      </div>
      <div className="text-[13px] font-semibold tracking-tight leading-snug line-clamp-2"
           style={{ color: MAGENTA }}>
        {idea?.aignoz_formula || idea?.problem || "—"}
      </div>
      <div className="flex flex-wrap gap-1">
        {idea?.problem_source && (
          <span className="text-[9px] px-1.5 py-0.5 rounded border border-white/[0.12] text-white/60">
            {idea.problem_source}
          </span>
        )}
        {idea?.white_space_cluster && (
          <span className="text-[9px] px-1.5 py-0.5 rounded border border-teal-400/30 text-teal-200/85">
            {idea.white_space_cluster}
          </span>
        )}
      </div>
      <div className="mt-auto pt-1.5 flex items-baseline justify-between">
        <span className="text-[9px] uppercase tracking-[0.14em] text-white/40">Sellable Idea Price</span>
        <span className="text-sm mono" style={{ color: MAGENTA }}>
          {formatUsd(sellableUsd)} <span className="text-white/40 text-[10px]">/ {formatInr(sellableInr)}</span>
        </span>
      </div>
    </div>
  );
}

// ===========================================================================
// Funnel ring — five concentric arcs, 1 per stage
// ===========================================================================
function FunnelRing({ counts, target }) {
  const safeTarget = target || 100;
  const size = 130;
  const cx = size / 2;
  const cy = size / 2;
  const colors = ["#22D3EE", "#34D399", "#A78BFA", "#FBBF24", MAGENTA];
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {STAGES.map((s, i) => {
        const r = 50 - i * 8;
        const pct = Math.min(1, Math.max(0, (counts[s.key] || 0) / safeTarget));
        const C = 2 * Math.PI * r;
        return (
          <g key={s.key} transform={`rotate(-90 ${cx} ${cy})`}>
            <circle cx={cx} cy={cy} r={r} stroke="rgba(255,255,255,0.07)" strokeWidth={4} fill="none" />
            <circle
              cx={cx} cy={cy} r={r}
              stroke={colors[i]}
              strokeWidth={4}
              fill="none"
              strokeLinecap="round"
              strokeDasharray={`${pct * C} ${C}`}
              style={{ transition: "stroke-dasharray 0.6s ease" }}
            />
          </g>
        );
      })}
      <text
        x={cx} y={cy - 4} textAnchor="middle" dominantBaseline="middle"
        fontFamily="ui-monospace, SFMono-Regular, monospace" fontSize="11"
        fill="rgba(255,255,255,0.55)"
      >
        {STAGES.reduce((s, st) => s + (counts[st.key] || 0), 0)}
      </text>
      <text
        x={cx} y={cy + 11} textAnchor="middle" dominantBaseline="middle"
        fontFamily="ui-monospace, SFMono-Regular, monospace" fontSize="8"
        fill="rgba(255,255,255,0.35)"
      >
        / {safeTarget * 5}
      </text>
    </svg>
  );
}

// ===========================================================================
// Empty state CTA
// ===========================================================================
function EmptyRunCta({ onRun }) {
  return (
    <div
      data-testid="landing-empty-cta"
      className="rounded-2xl border border-white/[0.08] bg-white/[0.014] p-10 text-center"
    >
      <Factory className="w-9 h-9 mx-auto mb-3" style={{ color: MAGENTA }} />
      <div className="text-2xl font-semibold tracking-tight">
        Your first <span style={{ color: MAGENTA }}>100</span> ideas are 90 seconds away
      </div>
      <p className="text-sm text-white/55 mt-2 max-w-md mx-auto">
        Click below — we'll stream the full 5-stage funnel live, then surface the top 10 with V3 scores
        and sellable prices.
      </p>
      <Button
        onClick={onRun}
        size="lg"
        className="mt-5 gap-2 text-black"
        style={{ background: MAGENTA }}
        data-testid="landing-empty-run-btn"
      >
        <Rocket className="w-4 h-4" /> Run Factory now
      </Button>
    </div>
  );
}

function SkeletonRunCard() {
  return (
    <div className="rounded-2xl border border-white/[0.06] bg-white/[0.014] p-8 animate-pulse">
      <div className="h-3 w-32 bg-white/10 rounded mb-3" />
      <div className="h-10 w-24 bg-white/10 rounded mb-2" />
      <div className="h-3 w-48 bg-white/10 rounded mb-6" />
      <div className="grid grid-cols-3 gap-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-28 rounded-lg bg-white/[0.04]" />
        ))}
      </div>
    </div>
  );
}

// ===========================================================================
// Story strip — 4 step cards
// ===========================================================================
function StoryStrip({ persona }) {
  const navigate = useNavigate();
  const byKey = {
    matrix:  { key: "matrix",  label: "1,500 cells",         sub: "Innovation Matrix",     icon: Atom,    to: "/matrix",       color: "#A78BFA" },
    sources: { key: "sources", label: "10 Sources",          sub: "of billion-dollar problems", icon: Compass, to: "/sources",      color: TEAL },
    factory: { key: "factory", label: "100 ventures",        sub: "ranked by V3 5-criteria filter", icon: Factory, to: "/factory",      color: MAGENTA },
    top10:   { key: "top10",   label: "Top 10",              sub: "with sellable price",   icon: Trophy,  to: "/factory#top10", color: "#FBBF24" },
  };
  const order = personaStoryOrder(persona);
  const items = order.map((k) => byKey[k]);
  return (
    <section className="px-8 pb-16">
      <div className="max-w-[1200px] mx-auto">
        <div className="text-[10px] uppercase tracking-[0.18em] text-white/45 mb-3">How it works</div>
        <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-1">
          From a 3-axis cube to a portfolio you can sell
        </h2>
        <p className="text-sm text-white/55 italic mb-6 max-w-[60ch]">
          Four steps — each is its own dedicated workspace. Click any card to dive in.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {items.map((s, i) => (
            <motion.button
              key={s.key}
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: i * 0.04 }}
              onClick={() => navigate(s.to)}
              data-testid={`landing-story-card-${s.key}`}
              className="text-left rounded-xl border border-white/[0.08] bg-white/[0.014] p-4 hover:border-white/[0.18]
                         transition flex flex-col gap-2 group"
            >
              <div className="flex items-center justify-between">
                <div className="w-9 h-9 rounded-lg border flex items-center justify-center"
                     style={{ borderColor: `${s.color}50`, background: `${s.color}14` }}>
                  <s.icon className="w-4 h-4" style={{ color: s.color }} />
                </div>
                <span className="text-[9px] mono text-white/35">STEP {i + 1}</span>
              </div>
              <div className="mt-1">
                <div className="text-base font-semibold tracking-tight" style={{ color: s.color }}>
                  {s.label}
                </div>
                <div className="text-[12px] text-white/65 leading-snug">{s.sub}</div>
              </div>
              <div className="mt-auto pt-2 text-[10px] mono uppercase tracking-[0.14em] text-white/40 inline-flex items-center gap-1 group-hover:text-white/80 transition">
                Open <ArrowRight className="w-3 h-3" />
              </div>
            </motion.button>
          ))}
        </div>
      </div>
    </section>
  );
}

// ===========================================================================
// Founder strip — compact reuse-ish (does NOT duplicate the full footer in pricing.jsx)
// ===========================================================================
function FounderStrip() {
  return (
    <section
      data-testid="landing-founder-strip"
      className="px-8 py-12 border-t border-white/[0.06] bg-white/[0.012]"
    >
      <div className="max-w-[900px] mx-auto text-center">
        <img src="/aignoz-logo.png" alt="Aignoz" className="h-9 w-9 mx-auto mb-3 object-contain opacity-90" />
        <div className="text-sm font-semibold text-white/90">
          Dr. Sriinidhi BV, MBBS, MD
        </div>
        <div className="text-[12px] text-white/55">
          Founder &amp; CEO, Aignoz Healthcare Solutions
        </div>
        <p className="text-[12px] text-white/45 italic mt-3 max-w-[58ch] mx-auto">
          &ldquo;Building the operating system for healthcare innovation.&rdquo;
        </p>
        <div className="mt-5 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[11px]">
          <a href="https://www.linkedin.com/in/dr-sriinidhi-bv-a953637/"
             target="_blank" rel="noreferrer"
             className="inline-flex items-center gap-1.5 text-white/55 hover:text-white">
            <Linkedin className="w-3 h-3" /> LinkedIn
          </a>
          <a href="https://www.aignoz.com/" target="_blank" rel="noreferrer"
             className="inline-flex items-center gap-1.5 text-white/55 hover:text-white">
            <Globe className="w-3 h-3" /> aignoz.com
          </a>
          <a href="mailto:aignozai@gmail.com"
             className="inline-flex items-center gap-1.5 text-white/55 hover:text-white">
            <Mail className="w-3 h-3" /> aignozai@gmail.com
          </a>
          <span className="inline-flex items-center gap-1.5 text-white/45">
            <MapPin className="w-3 h-3" /> Bengaluru, India
          </span>
        </div>
      </div>
    </section>
  );
}
